import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { usePermissions } from '../hooks/usePermissions';
import { 
  Users, UserPlus, Mail, Shield, Crown, Eye, Edit, Edit3, Trash2, AlertCircle, CheckCircle, X,
  Plus, UserMinus, Megaphone, Code, Palette, DollarSign, Headphones, BarChart, FileText, 
  Briefcase, Target, Zap, Globe, Check
} from 'lucide-react';

// Icon component mapping for teams
const ICON_COMPONENTS = {
  Users, Megaphone, Code, Palette, DollarSign, Headphones, 
  BarChart, FileText, Briefcase, Target, Zap, Globe
};

const getIconComponent = (iconName) => ICON_COMPONENTS[iconName] || Users;

// Role configuration
const ROLES = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    description: 'Full access to everything including billing and team management'
  },
  admin: {
    label: 'Admin', 
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    description: 'Can manage users, integrations, and all data'
  },
  project_manager: {
    label: 'Project Manager',
    icon: Target,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    description: 'Can manage projects, tasks, and view team schedules'
  },
  board_member: {
    label: 'Board Member',
    icon: Shield,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    description: 'Access to board meetings, minutes, and governance',
    requiresBoardEnabled: true
  },
  editor: {
    label: 'Editor',
    icon: Edit,
    color: 'text-green-600', 
    bgColor: 'bg-green-100',
    description: 'Can view and edit clients and projects'
  },
  viewer: {
    label: 'Viewer',
    icon: Eye,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100', 
    description: 'Read-only access to clients and projects'
  }
};

// Team colors
const TEAM_COLORS = [
  '#6B7280', '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#6366F1'
];

// Team icons
const TEAM_ICONS = [
  { name: 'Users', label: 'Team' },
  { name: 'Megaphone', label: 'Marketing' },
  { name: 'Code', label: 'Engineering' },
  { name: 'Palette', label: 'Design' },
  { name: 'DollarSign', label: 'Sales' },
  { name: 'Headphones', label: 'Support' },
  { name: 'BarChart', label: 'Analytics' },
  { name: 'FileText', label: 'Content' },
  { name: 'Briefcase', label: 'Business' },
  { name: 'Target', label: 'Goals' },
  { name: 'Zap', label: 'Operations' },
  { name: 'Globe', label: 'Global' },
];

const InviteUserModal = ({ isOpen, onClose, onInvite, boardEnabled = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'viewer'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
    }
  }, [isOpen]);

  // Filter roles based on board enabled status
  const availableRoles = Object.entries(ROLES).filter(([key, role]) => {
    // Never show owner role
    if (key === 'owner') return false;
    // Only show board_member if board is enabled
    if (role.requiresBoardEnabled && !boardEnabled) return false;
    return true;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      setError(null);
      await onInvite(formData);
      setFormData({ firstName: '', lastName: '', email: '', role: 'viewer' });
      onClose();
    } catch (err) {
      console.error('Invitation failed:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to send invitation. Please try again.';
      setError(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-gray-900">Invite Team Member</h3>
          <button
            onClick={() => {
              setError(null);
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#ffffff' }}>
              First Name
            </label>
            <input
              type="text"
              required
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 !placeholder-gray-500"
              style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
              placeholder="John"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#ffffff' }}>
              Last Name
            </label>
            <input
              type="text"
              required
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 !placeholder-gray-500"
              style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
              placeholder="Doe"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#ffffff' }}>
              Email Address
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900 !placeholder-gray-500"
              style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
              placeholder="colleague@company.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#ffffff' }}>
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent !text-gray-900"
              style={{ color: '#1f2937', backgroundColor: '#ffffff' }}
            >
              {availableRoles.map(([key, role]) => (
                <option key={key} value={key}>{role.label}</option>
              ))}
            </select>
            <p className="text-xs mt-1" style={{ color: '#d1d5db' }}>
              {ROLES[formData.role]?.description}
            </p>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg transition-colors hover:bg-gray-50"
              style={{ color: '#374151' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
              style={{ color: '#ffffff' }}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TeamMember = ({ member, currentUser, onUpdateMember, onRemoveMember, canManageTeam }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editRole, setEditRole] = useState(member.role);
  
  const RoleIcon = ROLES[member.role]?.icon || Eye;
  const isCurrentUser = member.user.id === currentUser.id;
  // Use permission-based check instead of role check
  const canManage = canManageTeam;
  const canEditThisMember = canManage && !isCurrentUser && member.role !== 'owner';

  const handleSaveEdit = async () => {
    if (editRole !== member.role) {
      await onUpdateMember(member.id, editRole);
    }
    setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
          <span className="text-white font-bold text-lg">
            {member.user.name?.charAt(0) || 'U'}
          </span>
        </div>
        
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center space-x-2">
            <span>{member.user.name}</span>
            {isCurrentUser && (
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                You
              </span>
            )}
          </h3>
          <p className="text-sm text-gray-600">{member.user.email}</p>
          <p className="text-xs text-gray-500">
            Joined {new Date(member.joined_at).toLocaleDateString()}
          </p>
        </div>
      </div>
      
      <div className="flex items-center space-x-3">
        {isEditing ? (
          <div className="flex items-center space-x-2">
            <select
              value={editRole}
              onChange={(e) => setEditRole(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              {Object.entries(ROLES).map(([key, role]) => (
                key !== 'owner' && (
                  <option key={key} value={key}>{role.label}</option>
                )
              ))}
            </select>
            <button
              onClick={handleSaveEdit}
              className="px-2 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              className="px-2 py-1 text-gray-600 hover:text-gray-800 text-sm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${ROLES[member.role]?.bgColor}`}>
              <RoleIcon className={`w-4 h-4 ${ROLES[member.role]?.color}`} />
              <span className={`text-sm font-medium ${ROLES[member.role]?.color}`}>
                {ROLES[member.role]?.label}
              </span>
            </div>
            
            {canEditThisMember && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onRemoveMember(member.id, member.user.name)}
                  className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const TeamManagement = ({ user }) => {
  const [teamData, setTeamData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Use permissions hook for access control
  const { canManageTeam, hasPermission, isAdmin } = usePermissions();
  
  // Teams (sub-teams) state
  const [subTeams, setSubTeams] = useState([]);
  const [subTeamsEnabled, setSubTeamsEnabled] = useState(false);
  const [canManageSubTeams, setCanManageSubTeams] = useState(false);
  const [selectedSubTeam, setSelectedSubTeam] = useState(null);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingSubTeam, setEditingSubTeam] = useState(null);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [subTeamMessage, setSubTeamMessage] = useState(null);

  // Governance settings - for showing/hiding Board Member role
  const [boardEnabled, setBoardEnabled] = useState(false);

  const fetchTeamData = async () => {
    try {
      const response = await api.get('/team');
      setTeamData(response.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch governance settings to know if Board Member role should be shown
  const fetchGovernanceSettings = useCallback(async () => {
    try {
      const response = await api.get('/governance/settings');
      setBoardEnabled(response.data.board_enabled || false);
    } catch (error) {
      // Silently fail - board_enabled will stay false
      console.debug('Could not fetch governance settings:', error);
    }
  }, []);

  const fetchSubTeams = useCallback(async () => {
    try {
      const response = await api.get('/sub-teams');
      setSubTeams(response.data.sub_teams || []);
      setSubTeamsEnabled(response.data.enabled || false);
      setCanManageSubTeams(response.data.can_manage || false);
    } catch (error) {
      console.error('Failed to fetch sub-teams:', error);
    }
  }, []);

  useEffect(() => {
    fetchTeamData();
    fetchSubTeams();
    fetchGovernanceSettings();
  }, [fetchSubTeams]);

  // Clear messages after delay
  useEffect(() => {
    if (subTeamMessage) {
      const timer = setTimeout(() => setSubTeamMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [subTeamMessage]);

  const handleInviteUser = async (inviteData) => {
    await api.post('/team/invite', inviteData);
    await fetchTeamData();
  };

  const handleUpdateMember = async (memberId, newRole) => {
    await api.put(`/team/member/${memberId}`, {
      role: newRole,
      permissions: []
    });
    await fetchTeamData();
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) {
      await api.delete(`/team/member/${memberId}`);
      await fetchTeamData();
    }
  };

  const handleResendInvitation = async (invitationId, email) => {
    try {
      await api.post(`/team/invitation/${invitationId}/resend`, {});
      alert(`Invitation email resent to ${email}`);
      await fetchTeamData();
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation email. Please try again.');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    await api.delete(`/team/invitation/${invitationId}`);
    await fetchTeamData();
  };

  // Sub-teams handlers
  const handleToggleSubTeams = async () => {
    setTogglingEnabled(true);
    try {
      const newEnabled = !subTeamsEnabled;
      await api.put('/sub-teams/settings/enabled', { enabled: newEnabled });
      setSubTeamsEnabled(newEnabled);
      setSubTeamMessage({ type: 'success', text: newEnabled ? 'Teams enabled' : 'Teams disabled' });
      if (!newEnabled) setSelectedSubTeam(null);
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: 'Failed to update setting' });
    } finally {
      setTogglingEnabled(false);
    }
  };

  const handleCreateSubTeam = async (data) => {
    try {
      await api.post('/sub-teams', data);
      setSubTeamMessage({ type: 'success', text: `Team "${data.name}" created` });
      setShowTeamModal(false);
      fetchSubTeams();
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create team' });
    }
  };

  const handleUpdateSubTeam = async (teamId, data) => {
    try {
      await api.put(`/sub-teams/${teamId}`, data);
      setSubTeamMessage({ type: 'success', text: 'Team updated' });
      setEditingSubTeam(null);
      fetchSubTeams();
      if (selectedSubTeam?.id === teamId) fetchSubTeamDetails(teamId);
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: 'Failed to update team' });
    }
  };

  const handleDeleteSubTeam = async (teamId) => {
    if (!window.confirm('Delete this team?')) return;
    try {
      await api.delete(`/sub-teams/${teamId}`);
      setSubTeamMessage({ type: 'success', text: 'Team deleted' });
      if (selectedSubTeam?.id === teamId) setSelectedSubTeam(null);
      fetchSubTeams();
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: 'Failed to delete team' });
    }
  };

  const handleUpdateSubTeamMembers = async (teamId, memberIds) => {
    try {
      await api.put(`/sub-teams/${teamId}/members`, { member_ids: memberIds });
      setSubTeamMessage({ type: 'success', text: 'Team members updated' });
      fetchSubTeams();
      if (selectedSubTeam?.id === teamId) fetchSubTeamDetails(teamId);
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: 'Failed to update members' });
    }
  };

  const fetchSubTeamDetails = async (teamId) => {
    try {
      const response = await api.get(`/sub-teams/${teamId}`);
      setSelectedSubTeam(response.data);
    } catch (err) {
      setSubTeamMessage({ type: 'error', text: 'Failed to load team details' });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-16 bg-gray-100 rounded"></div>
            <div className="h-16 bg-gray-100 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const currentUserMembership = teamData?.members?.find(m => m.user.id === user.id);
  // Use permission-based check from usePermissions hook
  const canManageUsers = canManageTeam || hasPermission('team.invite_members') || hasPermission('team.change_roles');

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Team Management</span>
          </h2>
          <p className="text-gray-600">Manage team members and permissions</p>
        </div>
        
        {canManageUsers && (
          <button
            onClick={() => setShowInviteModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
          >
            <UserPlus className="w-4 h-4" />
            <span>Invite Member</span>
          </button>
        )}
      </div>

      {/* Team Info */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-medium !text-gray-900 mb-1">{teamData?.team?.name}</h3>
        <p className="text-sm text-gray-600">{teamData?.team?.description}</p>
      </div>

      {/* Team Members */}
      <div className="space-y-4 mb-6">
        <h3 className="font-medium text-gray-900">Team Members ({teamData?.members?.length || 0})</h3>
        
        {teamData?.members?.length > 0 ? (
          <div className="space-y-3">
            {teamData.members.map((member) => (
              <TeamMember
                key={member.id}
                member={member}
                currentUser={currentUserMembership}
                onUpdateMember={handleUpdateMember}
                onRemoveMember={handleRemoveMember}
                canManageTeam={canManageUsers}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No team members yet</h3>
            <p className="text-gray-600 mb-4">Invite colleagues to start collaborating</p>
            {canManageUsers && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                Invite Your First Member
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pending Invitations */}
      {teamData?.pending_invitations?.length > 0 && canManageUsers && (
        <div className="space-y-4">
          <h3 className="font-medium text-gray-900">Pending Invitations ({teamData.pending_invitations.length})</h3>
          <div className="space-y-2">
            {teamData.pending_invitations.map((invitation) => {
              const isExpired = invitation.is_expired || (invitation.expires_at && new Date(invitation.expires_at) < new Date());
              return (
              <div key={invitation.id} className={`flex items-center justify-between p-3 rounded-lg ${isExpired ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center space-x-3">
                  <Mail className={`w-4 h-4 ${isExpired ? 'text-red-600' : 'text-yellow-600'}`} />
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">{invitation.email}</span>
                      <span className="text-xs text-gray-500">as {ROLES[invitation.role]?.label}</span>
                    </div>
                    {invitation.first_name && invitation.last_name && (
                      <span className="text-xs text-gray-500">
                        {invitation.first_name} {invitation.last_name}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`text-xs font-medium ${isExpired ? 'text-red-600' : 'text-yellow-600'}`}>
                    {isExpired ? 'Expired' : 'Expires'} {new Date(invitation.expires_at).toLocaleDateString()}
                  </span>
                  <button
                    onClick={() => handleResendInvitation(invitation.id, invitation.email)}
                    className="px-3 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                    title={isExpired ? 'Refresh invitation link and send a new email' : 'Resend invitation email with a fresh link'}
                  >
                    {isExpired ? 'Refresh & Resend' : 'Resend'}
                  </button>
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                    title="Cancel invitation"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Teams (Sub-teams) Section */}
      {canManageSubTeams && (
        <div className="mt-10 pt-8 border-t border-gray-200">
          {/* Section Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Teams</h3>
                <p className="text-sm text-gray-500">Organize users into teams</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">{subTeamsEnabled ? 'Enabled' : 'Disabled'}</span>
              <button
                onClick={handleToggleSubTeams}
                disabled={togglingEnabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  subTeamsEnabled ? 'bg-purple-600' : 'bg-gray-300'
                } ${togglingEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  subTeamsEnabled ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
          </div>

          {/* Message */}
          {subTeamMessage && (
            <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
              subTeamMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {subTeamMessage.type === 'success' ? <Check className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {subTeamMessage.text}
            </div>
          )}

          {subTeamsEnabled && (
            <div className="bg-gray-50 rounded-xl p-5 mt-4">
              {/* Create Team Button */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-700">
                  {subTeams.length} {subTeams.length === 1 ? 'team' : 'teams'}
                </span>
                <button
                  onClick={() => setShowTeamModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  New Team
                </button>
              </div>

              {/* Teams List */}
              {subTeams.length === 0 ? (
                <div className="text-center py-8 bg-white rounded-lg border border-dashed border-gray-300">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm mb-1">No teams created yet</p>
                  <p className="text-gray-400 text-xs">Create teams to organize your users</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {subTeams.map(team => {
                    const IconComponent = getIconComponent(team.icon);
                    const isSelected = selectedSubTeam?.id === team.id;
                    return (
                      <div
                        key={team.id}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-all bg-white ${
                          isSelected ? 'border-purple-500 shadow-sm' : 'border-transparent hover:border-gray-200'
                        }`}
                        onClick={() => fetchSubTeamDetails(team.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-9 h-9 rounded-lg flex items-center justify-center" 
                              style={{ backgroundColor: `${team.color}15` }}
                            >
                              <IconComponent className="w-4 h-4" style={{ color: team.color }} />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 text-sm">{team.name}</div>
                              <div className="text-xs text-gray-500">{team.member_count} {team.member_count === 1 ? 'member' : 'members'}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => { e.stopPropagation(); setEditingSubTeam(team); }}
                              className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                              title="Edit team"
                            >
                              <Edit3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteSubTeam(team.id); }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete team"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Selected Team Details */}
              {selectedSubTeam && (
                <div className="mt-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${selectedSubTeam.color}15` }}
                      >
                        {(() => {
                          const Icon = getIconComponent(selectedSubTeam.icon);
                          return <Icon className="w-4 h-4" style={{ color: selectedSubTeam.color }} />;
                        })()}
                      </div>
                      <h4 className="font-semibold text-gray-900">{selectedSubTeam.name}</h4>
                    </div>
                    <button 
                      onClick={() => setSelectedSubTeam(null)} 
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">Members</span>
                      <span className="text-xs text-gray-500">{selectedSubTeam.members?.length || 0} total</span>
                    </div>
                    
                    {selectedSubTeam.members?.length === 0 ? (
                      <div className="text-center py-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">No members assigned</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {selectedSubTeam.members?.map(member => (
                          <div key={member.id} className="flex items-center justify-between p-2.5 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors group">
                            <div className="flex items-center gap-2.5">
                              {member.avatar_url ? (
                                <img src={member.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover" />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-xs text-white font-medium">
                                  {member.name?.charAt(0)?.toUpperCase()}
                                </div>
                              )}
                              <div>
                                <span className="text-sm font-medium text-gray-900">{member.name}</span>
                                {member.email && (
                                  <span className="text-xs text-gray-500 block">{member.email}</span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                const ids = selectedSubTeam.members.filter(m => m.id !== member.id).map(m => m.id);
                                handleUpdateSubTeamMembers(selectedSubTeam.id, ids);
                              }}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                              title="Remove from team"
                            >
                              <UserMinus className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Add Member Dropdown */}
                  <div className="pt-3 border-t border-gray-100">
                    <SubTeamMemberSelector
                      allMembers={teamData?.members || []}
                      currentMembers={selectedSubTeam.members || []}
                      onAddMember={(memberId) => {
                        const ids = [...(selectedSubTeam.members || []).map(m => m.id), memberId];
                        handleUpdateSubTeamMembers(selectedSubTeam.id, ids);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInviteUser}
        boardEnabled={boardEnabled}
      />

      {/* Team Create/Edit Modal */}
      {(showTeamModal || editingSubTeam) && (
        <SubTeamFormModal
          team={editingSubTeam}
          onClose={() => { setShowTeamModal(false); setEditingSubTeam(null); }}
          onSave={editingSubTeam 
            ? (data) => handleUpdateSubTeam(editingSubTeam.id, data)
            : handleCreateSubTeam
          }
        />
      )}
    </div>
  );
};

// Sub-team member selector component
const SubTeamMemberSelector = ({ allMembers, currentMembers, onAddMember }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentIds = currentMembers.map(m => m.id);
  const available = allMembers.filter(m => !currentIds.includes(m.user?.id || m.id));

  if (available.length === 0) {
    return <span className="text-xs text-gray-400">All members added</span>;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700"
      >
        <UserPlus className="w-4 h-4" /> Add Member
      </button>
      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
          {available.map(m => {
            const member = m.user || m;
            return (
              <button
                key={member.id}
                onClick={() => { onAddMember(member.id); setIsOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left text-sm"
              >
                {member.avatar_url ? (
                  <img src={member.avatar_url} alt="" className="w-6 h-6 rounded-full" />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-xs text-purple-600">
                    {member.name?.charAt(0)}
                  </div>
                )}
                <span className="text-gray-900">{member.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Sub-team form modal
const SubTeamFormModal = ({ team, onClose, onSave }) => {
  const [name, setName] = useState(team?.name || '');
  const [color, setColor] = useState(team?.color || TEAM_COLORS[0]);
  const [icon, setIcon] = useState(team?.icon || 'Users');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), color, icon });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-sm p-5 m-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{team ? 'Edit Team' : 'Create Team'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              placeholder="e.g., Marketing"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex flex-wrap gap-2">
              {TEAM_COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 ${color === c ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Icon</label>
            <div className="flex flex-wrap gap-2">
              {TEAM_ICONS.map(({ name: iconName, label }) => {
                const IconComp = getIconComponent(iconName);
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => setIcon(iconName)}
                    title={label}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      icon === iconName ? 'bg-purple-100 text-purple-600 ring-2 ring-purple-500' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <IconComp className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-3 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving || !name.trim()} className="flex-1 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamManagement;